import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/PageHeader'
import { GlobalNoticeCard } from '@/components/settings/GlobalNoticeCard'
import { GlobalReportBccCard } from '@/components/settings/GlobalReportBccCard'
import { globalNoticeQuery, globalReportBccQuery, sessionQuery } from '@/lib/queries'
import { isStaff } from '@/lib/viewer'

/**
 * Clipbook-wide settings (CLI-625) — the staff toolbox's one page for things that apply to every
 * report of every account. The global notice is the first. Gated on `MANAGE_ALL_ACCOUNTS` in the
 * nav (UX) and on the PUT (the boundary); the read is open to any session, so a non-staff visitor
 * by URL sees the notice read-only rather than an error. The global report Bcc (CLI-850) is
 * staff-only both ways, so it is neither fetched nor shown for anyone else.
 */
export const Route = createFileRoute('/app/settings')({
  loader: async ({ context }) => {
    const [, session] = await Promise.all([
      context.queryClient.ensureQueryData(globalNoticeQuery),
      context.queryClient.ensureQueryData(sessionQuery),
    ])
    if (isStaff(session.user)) await context.queryClient.ensureQueryData(globalReportBccQuery)
  },
  component: SettingsPage,
})

function SettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery)
  const { data: notice } = useSuspenseQuery(globalNoticeQuery)
  return (
    <div className="mx-auto flex w-full max-w-4xl animate-fade-in flex-col gap-6">
      <PageHeader
        title="Settings"
        description="What applies to every report of every account. Clipbook staff only."
      />
      {/* Keyed on the save stamp: a successful save remounts the card on what was saved. */}
      <GlobalNoticeCard
        key={notice.updatedAt ?? 'unsaved'}
        notice={notice}
        canEdit={isStaff(session.user)}
      />
      {isStaff(session.user) && <ReportBcc />}
    </div>
  )
}

function ReportBcc() {
  const { data: bcc } = useSuspenseQuery(globalReportBccQuery)
  return <GlobalReportBccCard key={bcc.updatedAt ?? 'unsaved'} bcc={bcc} />
}
